import React, { useCallback } from 'react';
import { ChipSelector } from './ChipSelector';

// ── Rating options ─────────────────────────────────────────────────
const RATING_OPTIONS = [
  { value: '0', label: 'Never' },
  { value: '1', label: 'Rarely' },
  { value: '2', label: 'Sometimes' },
  { value: '3', label: 'Often' },
  { value: '4', label: "Don't Know" },
];

// ── Styles ─────────────────────────────────────────────────────────
const sectionStyle = { marginBottom: 32, paddingBottom: 16, borderBottom: '1px solid #e5e7eb' };
const sectionTitle = { fontSize: '1.25rem', fontWeight: 700, color: '#1e3a5f', marginBottom: 12 };
const fieldGap = { marginBottom: 16 };
const labelStyle = { fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' };
const inputStyle = {
  width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db',
  fontSize: '0.875rem', fontFamily: 'inherit', color: '#1a202c',
};
const textareaStyle = { ...inputStyle, minHeight: 80, resize: 'vertical' };

const pairCard = {
  padding: '14px 16px', borderRadius: 10, border: '1px solid #e5e7eb',
  backgroundColor: '#fafafa', marginBottom: 10,
};
const questionText = {
  fontSize: '0.8125rem', color: '#1f2937', lineHeight: 1.5, marginBottom: 10, fontWeight: 500,
};
const ratingRow = {
  display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
};
const periodLabel = {
  fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.03em',
  padding: '2px 8px', borderRadius: 4, flexShrink: 0, whiteSpace: 'nowrap',
};

// ── ACE-Informant (child) paired items ─────────────────────────────
const PAIRS = [
  { child: 1, recent: 2, text: 'fail to give close attention to details or make careless mistakes (e.g. difficulty concentrating on schoolwork, tasks or chores)' },
  { child: 3, recent: 4, text: 'fail to sustain attention in tasks or activities (e.g. maintaining their focus over a long period when concentrating on tasks)' },
  { child: 5, recent: 6, text: 'not listen to what was being said to them (e.g. difficulty concentrating on what is said to them)' },
  { child: 7, recent: 8, text: 'fail to follow through on instructions or finish tasks (e.g. not completing chores, homework, or work activities)' },
  { child: 9, recent: 10, text: 'struggle to organise tasks and activities (e.g. difficulty structuring and planning activities)' },
  { child: 11, recent: 12, text: 'avoid or strongly dislike tasks requiring sustained mental effort (e.g. by delaying, negotiating, or refusing)' },
  { child: 13, recent: 14, text: 'lose things necessary for certain tasks or activities (e.g. misplaced or had difficulty finding belongings)' },
  { child: 15, recent: 16, text: 'become easily distracted by their own thoughts or by things going on around them' },
  { child: 17, recent: 18, text: 'become forgetful in their daily activities (e.g. forgetting items they needed for activities)' },
  { child: 19, recent: 20, text: 'fidget with hands or feet or squirm about when sitting down (e.g. unable to sit still, restless, fidgety)' },
  { child: 21, recent: 22, text: 'leave their seat in situations where they were expected to remain seated (e.g. in class, at the dinner table)' },
  { child: 23, recent: 24, text: 'be hyperactive or physically restless (e.g. overly active, running, climbing, having a need to always be doing something)' },
  { child: 25, recent: 26, text: 'have difficulty engaging quietly in leisure activities (e.g. could not settle, always noisy)' },
  { child: 27, recent: 28, text: 'be persistently active and "on the go" (e.g. always rushing around, always busy, difficulty relaxing)' },
  { child: 29, recent: 30, text: 'talk excessively' },
  { child: 31, recent: 32, text: 'blurt out answers before questions had been completed' },
  { child: 33, recent: 34, text: 'have difficulty waiting their turn' },
  { child: 35, recent: 36, text: 'interrupt or intrude on others (e.g. butting into conversations or activities)' },
];

// ── Component ──────────────────────────────────────────────────────
export default function ACEInformantForm({ responses, setResponses }) {
  const r = responses;
  const set = useCallback((key, val) => setResponses(prev => ({ ...prev, [key]: val })), [setResponses]);

  return (
    <div style={{ maxWidth: 800 }}>

      {/* ─── Paired Items ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>ADHD Symptom Questions</h2>
        <p style={{ fontSize: '0.8125rem', color: '#6b7280', marginBottom: 8 }}>
          For each question, rate how much this applied to the person <strong>as a child</strong> and <strong>in the past few months/years</strong>.
        </p>
        <p style={{ fontSize: '1rem', fontWeight: 600, color: '#1e3a5f', marginBottom: 12 }}>Does your child...</p>

        {PAIRS.map((pair, idx) => (
          <div key={idx} style={pairCard}>
            <p style={questionText}>{idx + 1}. ...{pair.text}</p>
            <div style={ratingRow}>
              <span style={{ ...periodLabel, backgroundColor: '#dbeafe', color: '#1e40af' }}>As a child</span>
              <div style={{ flex: 1 }}>
                <ChipSelector options={RATING_OPTIONS} value={r[`item_${pair.child}`] || ''} onChange={v => set(`item_${pair.child}`, v)} />
              </div>
            </div>
            <div style={ratingRow}>
              <span style={{ ...periodLabel, backgroundColor: '#fef3c7', color: '#92400e' }}>Recently (past 6 months)</span>
              <div style={{ flex: 1 }}>
                <ChipSelector options={RATING_OPTIONS} value={r[`item_${pair.recent}`] || ''} onChange={v => set(`item_${pair.recent}`, v)} />
              </div>
            </div>
            <textarea style={{ ...textareaStyle, marginTop: 8, minHeight: 40, fontSize: '0.85rem' }} value={r[`item_${pair.child}_examples`] || ''} onChange={e => set(`item_${pair.child}_examples`, e.target.value)} placeholder="Any examples you think would be helpful for us to know (optional)" />
          </div>
        ))}
      </div>

      {/* ─── Summary ─── */}
      <div style={{ marginBottom: 16 }}>
        <h2 style={sectionTitle}>Summary</h2>
        <div style={fieldGap}>
          <label style={labelStyle}>At what age did you first notice these symptoms or problems?</label>
          <input type="text" style={inputStyle} value={r.onset_age || ''} onChange={e => set('onset_age', e.target.value)} data-testid="ace-inf-onset" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Have these symptoms been present and caused difficulties over the past six months?</label>
          <textarea style={textareaStyle} value={r.duration_impact || ''} onChange={e => set('duration_impact', e.target.value)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>How have these symptoms impacted on academic, occupational and/or social functioning?</label>
          <textarea style={{ ...textareaStyle, minHeight: 100 }} value={r.functional_impact || ''} onChange={e => set('functional_impact', e.target.value)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Is there anything else you feel we should know or you would like to tell us?</label>
          <textarea style={{ ...textareaStyle, minHeight: 80 }} value={r.anything_else || ''} onChange={e => set('anything_else', e.target.value)} />
        </div>
      </div>
    </div>
  );
}
